import type { Reply } from 'models';
import { type RefObject, useRef } from 'react';
import { usePermissions } from 'permissions';
import type { InputRef, RichTextEditorBase } from 'ui';
import { type CommentMode, CreateCommentForm } from '../../../create-comment-form';
import { useContentObjectRepliesQuery } from '../../../queries';
import { Comments } from '../../comments';
import { CommentsCard } from '../../comments-card';
import { CommentsSkeleton } from '../../comments-skeleton';
import { useGetSocialEventComments } from './hooks';

export type CommentsWrapperSocialEventContentProperties = {
  card?: boolean;
  commentFormContainerRef?: RefObject<HTMLDivElement | null>;
  editorRef?: RefObject<null | RichTextEditorBase>;
  eventId: string;
  initialMode?: CommentMode;
  inputRef?: RefObject<InputRef | null>;
  scrollOnMount?: boolean;
};

// eslint-disable-next-line @typescript-eslint/require-await -- for now social events don't support mentions
const onRequestMentions = async () => [];

const CommentsWrapperSocialEventContent = ({
  card,
  commentFormContainerRef: commentFormContainerRefProp,
  editorRef: editorRefProp,
  eventId,
  initialMode,
  inputRef: inputRefProp,
  scrollOnMount,
}: CommentsWrapperSocialEventContentProperties) => {
  const { canCommentSocialEvent, canDeleteSocialEventComment } = usePermissions();
  const editorRef = useRef<RichTextEditorBase>(null);
  const inputRef = useRef<InputRef>(null);
  const commentFormContainerRef = useRef<HTMLDivElement>(null);
  const getSocialEventComments = useGetSocialEventComments({ eventId });

  const {
    data: comments,
    isFetching,
    isLoading,
  } = useContentObjectRepliesQuery({
    id: eventId,
    options: {
      gcTime: 0,
    },
  });

  if ((isLoading && isFetching) || !comments) {
    return <CommentsSkeleton card={card} />;
  }

  const canComment = canCommentSocialEvent({});
  const canDeleteComment = (comment: Reply) => canDeleteSocialEventComment({ comment });
  const socialEventComments = getSocialEventComments({ comments });

  return card ? (
    <CommentsCard
      canComment={canComment}
      canDeleteComment={canDeleteComment}
      commentFormContainerRef={commentFormContainerRefProp ?? commentFormContainerRef}
      comments={socialEventComments}
      contentObjectId={eventId}
      contentObjectType="event"
      editorRef={editorRefProp ?? editorRef}
      inputRef={inputRefProp ?? inputRef}
      onRequestMentions={onRequestMentions}
    />
  ) : (
    <>
      <Comments
        canComment={canComment}
        canDeleteComment={canDeleteComment}
        comments={socialEventComments}
        contentObjectId={eventId}
        contentObjectType="event"
        createCommentForm={false}
        onRequestMentions={onRequestMentions}
      />
      {canComment ? (
        <CreateCommentForm
          commentFormContainerRef={commentFormContainerRefProp ?? commentFormContainerRef}
          contentObjectId={eventId}
          editorRef={editorRefProp ?? editorRef}
          initialMode={initialMode}
          inputRef={inputRefProp ?? inputRef}
          onRequestMentions={onRequestMentions}
          scrollOnMount={scrollOnMount}
        />
      ) : null}
    </>
  );
};

export default CommentsWrapperSocialEventContent;
